// Formatting and data transformation utilities

// Currency formatter
export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
})

// Currency formatter with decimals
export const currencyFormatterWithDecimals = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

// Convert DOB components to ISO format (YYYY-MM-DD)
export function toIsoDob(mm, dd, yyyy) {
  const m = String(mm).padStart(2, '0')
  const d = String(dd).padStart(2, '0')
  return `${yyyy}-${m}-${d}`
}

// Convert DOB components to US format (MM/DD/YYYY)
export function toUsDob(mm, dd, yyyy) {
  return `${mm}/${dd}/${yyyy}`
}

// Map credit score to Array credit bucket
export function mapScoreToArrayCredit(score) {
  if (score < 500) return 'credit_score_under_500'
  if (score <= 549) return 'credit_score_500_549'
  if (score <= 599) return 'credit_score_550_599'
  if (score <= 649) return 'credit_score_600_649'
  if (score <= 699) return 'credit_score_650_699'
  return 'credit_score_over_699'
}

// Map debt amount to verified debt bucket
export function mapDebtToVerified(debt) {
  if (debt < 10000) return 9949
  if (debt < 15000) return 14999
  if (debt < 20000) return 19999
  if (debt < 30000) return 29999
  if (debt <= 40000) return 40000
  return 43499
}

// Convert slider value to dollar amount for debt amount question
// Slider range: 0-380, maps to $5,000 - $100,000
export function sliderToDollarValue(value, formatter = currencyFormatter) {
  const numValue = Number(value)
  if (numValue === 0) {
    return 'Less than $5,000'
  } else if (numValue === 380) {
    return 'More than $100,000'
  } else {
    const numberValue = 5000 + (numValue * 250)
    return formatter.format(numberValue)
  }
}

// Convert slider value to raw number
export function sliderToNumberValue(value) {
  const numValue = Number(value)
  if (numValue === 0) {
    return 0
  } else if (numValue === 380) {
    return 101000
  } else {
    return 5000 + (numValue * 250)
  }
}

// Convert a debt amount to the closest slider position (0-380)
export function debtAmountToSliderValue(value) {
  if (value === null || value === undefined) return null

  const normalizedValue = String(value).trim().replace(/[$,\s]/g, '')
  if (!/^-?\d+(\.\d+)?$/.test(normalizedValue)) {
    return null
  }

  const debtAmount = Number(normalizedValue)
  if (!Number.isFinite(debtAmount)) return null
  if (debtAmount < 5000) return 0
  if (debtAmount > 100000) return 380

  let closestSliderValue = 1
  let closestDistance = Number.POSITIVE_INFINITY

  // Keep interior values on numeric slider points (1-379),
  // reserving 0 and 380 for the special end-cap buckets.
  for (let sliderValue = 1; sliderValue <= 379; sliderValue++) {
    const representedDebt = sliderToNumberValue(sliderValue)
    const distance = Math.abs(representedDebt - debtAmount)

    if (distance < closestDistance) {
      closestDistance = distance
      closestSliderValue = sliderValue
    }
  }

  return closestSliderValue
}

// Calculate background gradient for range slider
export function calculateSliderGradient(value, min, max) {
  const percentage = ((value - min) / (max - min)) * 100
  return `linear-gradient(to right, #3C91E6 0%, #3C91E6 ${percentage}%, #eee ${percentage}%, #eee 100%)`
}

// Parse Array debt response string to numeric values
export function parseArrayDebtResponse(arrayDebtResponse, formatter = currencyFormatterWithDecimals) {
  if (!arrayDebtResponse) {
    return { verified_debt: 0, noloArrayString: '' }
  }

  let lowEnd = 0
  let highEnd = 0
  let verified_debt = 0
  let noloArrayString = ''

  if (arrayDebtResponse.indexOf('over') !== -1) {
    // debt over number (+5k)
    const debtNumber = arrayDebtResponse.match(/\d.*/)
    const num = Number(debtNumber[0])
    lowEnd = num
    highEnd = num + 5000
    verified_debt = (lowEnd + highEnd) / 2
    noloArrayString = `Unsecured debt is over ${formatter.format(lowEnd)}. `
  } else if (arrayDebtResponse.indexOf('under') !== -1) {
    // debt under number (-5k)
    const debtNumber = arrayDebtResponse.match(/\d.*/)
    const num = Number(debtNumber[0])
    lowEnd = num - 100
    highEnd = num
    verified_debt = (lowEnd + highEnd) / 2
    noloArrayString = `Unsecured debt is under ${formatter.format(highEnd)}. `
  } else {
    // debt is between numbers
    const debtNumber = arrayDebtResponse.match(/\d.*/)
    if (debtNumber) {
      lowEnd = Number(debtNumber[0].split('_')[0])
      highEnd = Number(debtNumber[0].split('_')[1])
      verified_debt = highEnd
      noloArrayString = `Unsecured debt is between ${formatter.format(lowEnd)} and ${formatter.format(highEnd)}. `
    }
  }

  return { verified_debt, noloArrayString, lowEnd, highEnd }
}

// Clean phone number (remove non-digits and country code)
export function cleanPhoneNumber(phone) {
  let cleaned = (phone || '').replace(/-/g, '')
  if (cleaned.startsWith('1') || cleaned.startsWith('+1') || cleaned.startsWith(' 1')) {
    cleaned = cleaned.slice(-10)
  }
  return cleaned.length === 10 ? cleaned : null
}
